import React from "react";
import { ACTION_TYPE, TARGET, Manager, State, Actions } from "./types";

export function useManager<Values>(values: Values): Manager<Values> {
  const [state, dispatch] = React.useReducer(
    function reducer(
      state: State<Values>,
      action: Actions<Values>
    ): State<Values> {
      switch (action.type) {
        case ACTION_TYPE.SET_ERROR:
          return {
            ...state,
            errors: {
              ...state.errors,
              [action.key]: action.error,
            },
          };
        case ACTION_TYPE.SET_VALUE:
          return {
            ...state,
            values: {
              ...state.values,
              [action.key]: action.value,
            },
          };
        case ACTION_TYPE.SET_ERRORS:
          return {
            ...state,
            errors: {
              ...state.errors,
              ...action.errors,
            },
          };
        case ACTION_TYPE.SET_VALUES:
          return {
            ...state,
            values: {
              ...state.values,
              ...action.values,
            },
          };
        case ACTION_TYPE.RESET:
          return {
            values: {
              ...values,
            },
            errors: {},
          };
        default:
          return state;
      }
    },
    {
      values: {
        ...values,
      },
      errors: {},
    }
  );

  return {
    values: state.values,
    errors: state.errors,
    set: <Target extends TARGET, Keys extends keyof Values>(
      target: Target,
      payload: Target extends TARGET.ERROR
        ? [Keys, Error | undefined]
        : Target extends TARGET.VALUE
        ? [Keys, Values[Keys]]
        : Target extends TARGET.ERRORS
        ? { [K in keyof Values]?: Error | undefined }
        : Target extends TARGET.VALUES
        ? Partial<Values>
        : never
    ): void => {
      switch (target) {
        case TARGET.ERROR:
          {
            const [key, error] = payload as [Keys, Error | undefined];

            dispatch({
              type: ACTION_TYPE.SET_ERROR,
              key,
              error,
            });
          }
          break;
        case TARGET.VALUE:
          {
            const [key, value] = payload as [Keys, Values[Keys]];

            dispatch({
              type: ACTION_TYPE.SET_VALUE,
              key,
              value,
            });
          }
          break;
        case TARGET.ERRORS:
          {
            const errors = payload as {
              [K in keyof Values]?: Error | undefined;
            };

            dispatch({
              type: ACTION_TYPE.SET_ERRORS,
              errors,
            });
          }
          break;
        case TARGET.VALUES:
          {
            const values = payload as Partial<Values>;

            dispatch({
              type: ACTION_TYPE.SET_VALUES,
              values,
            });
          }
          break;
      }
    },
    reset: () => {
      dispatch({
        type: ACTION_TYPE.RESET,
      });
    },
  };
}
